import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { User } from '@/api/entities';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { Loader2, Search, ArrowLeft, RefreshCw, Gift } from 'lucide-react';
import { toast } from 'sonner';
import { TIER_THEME, TIERS } from '@/constants/tiers';

function laPacificYearNow() {
  return parseInt(
    new Intl.DateTimeFormat('en-US', {
      timeZone: 'America/Los_Angeles',
      year: 'numeric',
    }).format(new Date()),
    10,
  );
}

function yearSelectRange() {
  const y = laPacificYearNow();
  const out = [];
  for (let dy = -2; dy <= 2; dy++) out.push(y + dy);
  return out.reverse();
}

function TierPill({ tier }) {
  const t = tier || 'member';
  const theme = TIER_THEME[t] || TIER_THEME.member;
  const label = TIERS.find((row) => row.id === t)?.name ?? t;
  return (
    <span
      className={cn(
        'inline-flex px-2 py-0.5 rounded-full text-[10px] font-medium tracking-normal border max-w-[160px] truncate',
        theme.chip,
        theme.border,
      )}
      title={label}
    >
      {label}
    </span>
  );
}

function ClaimsHint({ count }) {
  const n = Number(count) || 0;
  if (n <= 0) return <span className="text-[9px] text-white/35">claims 0</span>;
  return <span className="text-[9px] text-[hsl(228,35%,58%)]/85">claims {n}</span>;
}

export default function AccountingRewards() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [capYear, setCapYear] = useState(() => laPacificYearNow());
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [busyKey, setBusyKey] = useState(null);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 280);
    return () => clearTimeout(t);
  }, [search]);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        toast.error('Authentication required');
        navigate('/');
        return;
      }
      const qs = new URLSearchParams({ capYear: String(capYear) });
      const res = await fetch(`/api/admin/rewards-fulfillment?${qs}`, {
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Failed to load');
      }
      const json = await res.json();
      setRows(Array.isArray(json.rows) ? json.rows : []);
    } catch (e) {
      console.error(e);
      toast.error(e.message || 'Could not load rewards fulfillment');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [capYear, navigate]);

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const me = await User.me();
        if (!me || me.role !== 'admin') {
          toast.error('Admin access required');
          navigate('/');
          return;
        }
      } catch {
        navigate('/');
        return;
      }
      if (cancel) return;
      await fetchRows();
    })();
    return () => { cancel = true; };
  }, [fetchRows, navigate]);

  const filteredRows = useMemo(() => {
    const q = debouncedSearch.toLowerCase();
    if (!q) return rows;
    return rows.filter((r) =>
      String(r.email || '').toLowerCase().includes(q) ||
      String(r.display_name || '').toLowerCase().includes(q),
    );
  }, [rows, debouncedSearch]);

  const patchFulfillment = async (userId, field, checked) => {
    const key = `${userId}-${field}`;
    setBusyKey(key);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch('/api/admin/rewards-fulfillment', {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ userId, capYear, field, value: checked }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Save failed');
      }
      await fetchRows();
    } catch (e) {
      toast.error(e.message || 'Could not save');
    } finally {
      setBusyKey(null);
    }
  };

  return (
    <div className="min-h-screen bg-black text-white">
      <div className="max-w-[1600px] mx-auto px-6 md:px-8 py-6">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-8 px-2 text-[11px] text-white/50 hover:text-white mb-4"
          onClick={() => navigate('/Accounting')}
        >
          <ArrowLeft className="w-3.5 h-3.5 mr-1 opacity-70" />
          Accounting
        </Button>

        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-5">
          <div className="min-w-0">
            <div className="flex items-center gap-2 mb-2">
              <Gift className="w-6 h-6 text-[hsl(228,35%,58%)]/90" strokeWidth={1.5} />
              <h1 className="text-3xl md:text-[32px] font-light text-white tracking-tight leading-none">
                Status rewards
              </h1>
            </div>
            <p className="text-white/60 text-[12.5px] font-normal leading-relaxed max-w-xl">
              Claim counts mirror{' '}
              <code className="text-white/65">defera_status_benefit_claims</code>. Green checkboxes record operations
              (e-gifts sent). Birthday and welcome are read-only once the ledger shows an award for this LA calendar year.
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="flex items-center gap-2">
              <span className="text-[10px] uppercase tracking-widest text-white/35 whitespace-nowrap">Cap year</span>
              <Select value={String(capYear)} onValueChange={(v) => setCapYear(parseInt(v, 10))}>
                <SelectTrigger className="bg-white/[0.03] border-white/10 text-white h-9 rounded-full text-[11px] font-normal px-3.5 min-w-[100px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {yearSelectRange().map((y) => (
                    <SelectItem key={y} value={String(y)}>
                      LA {y}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-9 rounded-full border-white/10 bg-white/[0.03] hover:bg-white/[0.07] text-white/80"
              disabled={loading}
              onClick={() => fetchRows()}
            >
              {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
              <span className="ml-1.5 text-[11px]">Refresh</span>
            </Button>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-2 mb-4">
          <div className="relative flex-1 min-w-0">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[hsl(228,35%,58%)]/65 pointer-events-none" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Filter by email or name…"
              className="bg-white/[0.03] border-white/10 text-white placeholder:text-white/35 pl-9 h-9 rounded-full text-xs font-normal focus-visible:ring-1 focus-visible:ring-[hsl(228,35%,58%)]/35"
            />
          </div>
        </div>

        <div className="flex items-center gap-3 flex-wrap text-[10px] uppercase tracking-[0.12em] text-white/35 mb-2 tabular-nums">
          <span>{filteredRows.length} member{filteredRows.length === 1 ? '' : 's'}</span>
          {!loading && <span className="text-white/20">·</span>}
          {!loading && <span>Snapshot year {capYear}</span>}
          {!loading && (
            <>
              <span className="text-white/20 hidden sm:inline">·</span>
              <span
                className="normal-case tracking-normal text-white/38 max-w-xl"
                title="Tier exp. ’27 uses only 2026 completed-trip qualifying dollars and standard thresholds—not admin_override or founding special rows."
              >
                Exp. tier ’27 = thresholds from CY 2026 QD → benefits eligible in calendar-year 2027.
              </span>
            </>
          )}
        </div>

        <div className="rounded-xl border border-white/10 overflow-hidden bg-white/[0.02] overflow-x-auto">
          {loading ? (
            <div className="flex items-center justify-center py-24">
              <Loader2 className="w-8 h-8 animate-spin text-[hsl(228,35%,58%)]" />
            </div>
          ) : (
            <table className="w-full text-left text-[11px] min-w-[1280px]">
              <thead>
                <tr className="border-b border-white/10 text-white/40 uppercase tracking-[0.1em] text-[9px]">
                  <th className="py-2.5 pl-4 pr-2 font-medium">Member</th>
                  <th className="py-2.5 px-2 font-medium" title="Defera Status perks active today (benefit window)">Tier</th>
                  <th
                    className="py-2.5 px-2 font-medium max-w-[150px]"
                    title="Earned tier from 2026 qualifying spend only—the tier you qualify for entering benefit calendar-year 2027. Ignores admin overrides."
                  >
                    Tier exp.
                    <span className="block mt-0.5 font-normal lowercase tracking-normal text-[8px] text-white/30">earn → CY 2027</span>
                  </th>
                  <th className="py-2.5 px-2 font-medium text-right">QD {capYear}</th>
                  <th className="py-2.5 px-2 font-medium text-right">Trips</th>
                  <th className="py-2.5 px-2 font-medium text-center">Birthday</th>
                  <th className="py-2.5 px-2 font-medium text-center">Welcome</th>
                  <th className="py-2.5 px-2 font-medium text-center">Starbucks</th>
                  <th className="py-2.5 px-2 font-medium text-center">Uber</th>
                  <th className="py-2.5 px-2 font-medium text-center">Seats</th>
                  <th className="py-2.5 px-2 pr-4 font-medium text-center">Baggage</th>
                </tr>
              </thead>
              <tbody>
                {filteredRows.map((row) => {
                  const name = row.display_name || row.email || row.user_id;
                  return (
                    <tr key={row.user_id} className="border-b border-white/[0.06] hover:bg-white/[0.03]">
                      <td className="py-2 pl-4 pr-2 align-top">
                        <div className="text-white/90 font-normal truncate max-w-[220px]" title={row.email}>{name}</div>
                        <div className="text-[9px] text-white/40 truncate max-w-[220px]" title={row.email}>{row.email}</div>
                      </td>
                      <td className="py-2 px-2 align-top">
                        <TierPill tier={row.active_tier} />
                      </td>
                      <td className="py-2 px-2 align-top">
                        <TierPill tier={row.tier_expected_2027} />
                      </td>
                      <td className="py-2 px-2 align-top text-right tabular-nums text-white/85">${Number(row.qd_cap_year || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}</td>
                      <td className="py-2 px-2 align-top text-right tabular-nums text-white/60">{Number(row.qt_cap_year ?? 0)}</td>

                      <td className="py-2 px-2 align-top">
                        <div className="flex flex-col items-center gap-1">
                          <Checkbox
                            checked={!!row.birthday_done}
                            disabled
                            className="opacity-90 border-white/20 data-[state=checked]:bg-[hsl(228,45%,22%)]"
                            title="Automatically set when tier birthday miles are awarded."
                          />
                          <span className="text-[8px] text-white/35 text-center leading-tight max-w-[64px]">system</span>
                        </div>
                      </td>
                      <td className="py-2 px-2 align-top">
                        <div className="flex flex-col items-center gap-1">
                          <Checkbox
                            checked={!!row.welcome_done}
                            disabled
                            className="opacity-90 border-white/20 data-[state=checked]:bg-[hsl(228,45%,22%)]"
                            title="Automatically set when a welcome-style admin_grant appears in LA calendar year."
                          />
                          <span className="text-[8px] text-white/35 text-center leading-tight max-w-[64px]">system</span>
                        </div>
                      </td>

                      <td className="py-2 px-2 align-top">
                        <div className="flex flex-col items-center gap-1">
                          <Checkbox
                            checked={!!row.manual_starbucks}
                            disabled={busyKey === `${row.user_id}-starbucks`}
                            onCheckedChange={(v) =>
                              patchFulfillment(row.user_id, 'starbucks', v === true)}
                            title="Fulfillment checkbox — independent of entitlement."
                            className="border-white/25 data-[state=checked]:bg-emerald-600/85"
                          />
                          <ClaimsHint count={row.claims_starbucks} />
                        </div>
                      </td>
                      <td className="py-2 px-2 align-top">
                        <div className="flex flex-col items-center gap-1">
                          <Checkbox
                            checked={!!row.manual_uber}
                            disabled={busyKey === `${row.user_id}-uber`}
                            onCheckedChange={(v) =>
                              patchFulfillment(row.user_id, 'uber', v === true)}
                            className="border-white/25 data-[state=checked]:bg-emerald-600/85"
                          />
                          <ClaimsHint count={row.claims_uber} />
                        </div>
                      </td>
                      <td className="py-2 px-2 align-top">
                        <div className="flex flex-col items-center gap-1">
                          <Checkbox
                            checked={!!row.manual_seats}
                            disabled={busyKey === `${row.user_id}-seats`}
                            onCheckedChange={(v) =>
                              patchFulfillment(row.user_id, 'seats', v === true)}
                            className="border-white/25 data-[state=checked]:bg-emerald-600/85"
                          />
                          <ClaimsHint count={row.claims_seats} />
                        </div>
                      </td>
                      <td className="py-2 px-2 pr-4 align-top">
                        <div className="flex flex-col items-center gap-1">
                          <Checkbox
                            checked={!!row.manual_baggage}
                            disabled={busyKey === `${row.user_id}-baggage`}
                            onCheckedChange={(v) =>
                              patchFulfillment(row.user_id, 'baggage', v === true)}
                            className="border-white/25 data-[state=checked]:bg-emerald-600/85"
                          />
                          <ClaimsHint count={row.claims_bag} />
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
        {!loading && filteredRows.length === 0 && (
          <p className="text-center text-sm text-white/60 py-14">No matching members for this snapshot.</p>
        )}
      </div>
    </div>
  );
}
